/**
 * Segment detail view — shown when a canonical segment card is clicked.
 *
 * Renders:
 *   - back link
 *   - segment header (icon + name + description + export button)
 *   - two KPI cards (client count + average revenue)
 *   - loading skeleton / empty state / customer table
 *
 * Extracted from Segments.tsx during the Maintainability Sprint 2026-04-19
 * (Task 1.1 step 1.1.7). Behavior preserved byte-for-byte; coverage comes
 * from the page-level tests in ../__tests__/Segments.test.tsx — this file
 * doesn't yet have a dedicated unit test because the legacy tests already
 * exercise it via the full Segments page.
 */

import { ArrowLeft, Users, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@cieda/djayma-ui";
import { formatFCFA } from "@cieda/utils";
import { useSegmentCustomers } from "@/hooks/admin/useCRM";
import { EmptyState, KPICard } from "@/components/admin/shared";
import type { CanonicalSegmentRow } from "@/lib/admin-api/crm";
import { SEGMENT_STYLES, DEFAULT_STYLE } from "./constants";

interface SegmentDetailProps {
  segment: CanonicalSegmentRow;
  onBack: () => void;
}

export default function SegmentDetail({ segment, onBack }: SegmentDetailProps) {
  const { data, isLoading } = useSegmentCustomers(segment.key);
  const style = SEGMENT_STYLES[segment.key] ?? DEFAULT_STYLE;

  const handleExport = () => {
    toast.info("Export en cours de pr\u00e9paration...");
    // In production: trigger crmApi.segments.export(segment.key) and download the blob
  };

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Segments clients
      </button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-lg ${style.iconBg}`}
          >
            <Users className={`h-5 w-5 ${style.text}`} />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Segment — {segment.name}
            </h1>
            <p className="text-sm text-stone-500">{segment.description}</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleExport}
          className="gap-2 shrink-0"
        >
          <Download className="h-4 w-4" />
          Exporter le segment
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KPICard
          label="Clients dans ce segment"
          value={segment.customer_count}
          format="number"
        />
        <KPICard
          label="Revenu moyen"
          value={segment.avg_revenue_fcfa}
          format="currency"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
        {isLoading ? (
          <div className="space-y-3 p-4 animate-pulse">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 rounded bg-stone-100" />
            ))}
          </div>
        ) : !data || data.customers.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title="Aucun client dans ce segment"
            description="Ce segment ne contient aucun client pour le moment."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Client
                  </th>
                  <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Téléphone
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Total dépensé
                  </th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Dernière commande
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.customers.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-stone-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-medium text-amber-800">
                          {c.full_name.charAt(0)}
                        </div>
                        <span className="font-medium text-stone-900">
                          {c.full_name}
                        </span>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-stone-500">
                      {c.phone}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-stone-800">
                      {formatFCFA(c.total_spent_fcfa)}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-stone-500">
                      {c.last_order_date ?? "\u2014"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
